import { useState } from "react";
import { Menu, X, Phone, Instagram, Facebook, LogOut, User as UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LOGO_URL, INSTAGRAM_URL, FACEBOOK_URL, CONTACT_PHONE } from "@/lib/brand";
import { useAuth } from "@/lib/auth";

const NAV = [
  { label: "Compare", href: "/#compare" },
  { label: "EMI Calculator", href: "/#emi" },
  { label: "FAQ", href: "/#faq" },
  { label: "Contact", href: "/#contact" },
];

export const Header = ({ onCTA }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = pathname.startsWith("/admin");

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <img src={LOGO_URL} alt="Loan81" className="h-9 sm:h-10 w-auto object-contain" />
          </Link>

          {!isAdmin && (
            <nav className="hidden md:flex items-center gap-8">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                  className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                >
                  {n.label}
                </a>
              ))}
            </nav>
          )}

          <div className="hidden md:flex items-center gap-3">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="header-instagram"
              aria-label="Instagram"
              className="w-9 h-9 rounded-md text-slate-500 hover:text-emerald-600 hover:bg-slate-50 flex items-center justify-center transition-colors"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="header-facebook"
              aria-label="Facebook"
              className="w-9 h-9 rounded-md text-slate-500 hover:text-emerald-600 hover:bg-slate-50 flex items-center justify-center transition-colors"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href={`tel:${CONTACT_PHONE}`}
              data-testid="header-phone"
              className="hidden lg:flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-emerald-600 transition-colors pl-1"
            >
              <Phone className="w-4 h-4" />
              {CONTACT_PHONE}
            </a>

            {user ? (
              <div className="flex items-center gap-2 pl-2 border-l border-slate-200 ml-1">
                <div className="flex items-center gap-2 text-sm">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center"><UserIcon className="w-3.5 h-3.5 text-slate-600" /></div>
                  )}
                  <span className="hidden lg:inline font-medium text-slate-700 max-w-[120px] truncate">{user.name || user.email}</span>
                </div>
                <button data-testid="header-logout" onClick={handleLogout} className="p-1.5 text-slate-500 hover:text-red-600" title="Logout">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Button
                data-testid="header-cta-button"
                onClick={onCTA}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-md active:scale-95 transition-all"
              >
                Get Quote
              </Button>
            )}
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 rounded-md text-slate-700"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-4 space-y-2 border-t border-slate-200/60 pt-3">
            {!isAdmin && NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="block py-2 px-1 text-sm font-medium text-slate-700"
              >
                {n.label}
              </a>
            ))}
            <div className="flex items-center gap-3 pt-2">
              <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="p-2 text-slate-600"><Instagram className="w-4 h-4" /></a>
              <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" className="p-2 text-slate-600"><Facebook className="w-4 h-4" /></a>
              <a href={`tel:${CONTACT_PHONE}`} className="text-sm font-semibold text-slate-700">
                📞 {CONTACT_PHONE}
              </a>
            </div>
            {user ? (
              <Button onClick={handleLogout} variant="outline" className="w-full">Logout ({user.email})</Button>
            ) : (
              <Button
                data-testid="mobile-cta-button"
                onClick={() => { setOpen(false); onCTA?.(); }}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                Get Quote
              </Button>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
